import * as React from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import useUserStore from 'store/useUserStore';
import { Box, Typography } from '@mui/material';
import { AppProvider } from '@toolpad/core/AppProvider';
import DescriptionIcon from '@mui/icons-material/Description';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import type { Router, Session } from '@toolpad/core';
import { Account, AuthenticationContext, SessionContext } from '@toolpad/core';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GroupsIcon from '@mui/icons-material/Groups';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PostAddIcon from '@mui/icons-material/PostAdd';
import VisitRequestStatus from 'components/menu/VisitRequestStatus';
import VisitRequestDetails from 'components/menu/VisitRequestDetails';
import VisitRequest from 'components/menu/VisitRequest';
import SystemSettings from 'components/menu/SystemSettings';
import { useAuth } from 'contexts/AuthContext';
import ItemManagement from 'components/menu/ItemManagement';
import UserManagement from 'components/menu/UserManagement';
import GroupManagement from 'components/menu/GroupManagement';
import CustomMenu from './CustomMenu';
import EditUserInfo from 'components/User/EditUserInfo';
import Footer from '../Footer';
import EmployeeManagement from '../menu/EmployeeManagement';
import NotFound from '../../pages/NotFound';
import { TMeResponse, TResponseMeData } from '../../types/api';
import { getMeAPI } from '../../apis/auth/authAPI';
import { useEffect } from 'react';
import PolicyManagement from '../menu/PolicyManagement';
import Manual from '../menu/Manual';
import Faq from '../menu/Faq';
import NoticeBoard from 'pages/NoticeBoard';
import { customAppTheme } from './AppTheme';
import { useTranslation } from 'react-i18next';
import './commonLayout.css';
import NoticeDetail from 'pages/NoticeDetail';
import NoticeNew from 'pages/NoticeNew';
import NoticeEdit from 'pages/NoticeEdit';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import TvIcon from '@mui/icons-material/Tv';
import HistoryScreen from 'components/menu/HistoryScreen';
import HistoryUser from 'components/menu/HistoryUser';
import NoticePopup from 'pages/NoticePopup';
export interface TMenu {
  segment: string;
  title: string;
  icon: React.ReactNode;
  children?: TMenu[];
  isHide?: boolean;
}

export type NavigationState = Record<string, TMenu>;

const CommonLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useUserStore();
  const { login, logout } = useAuth();
  const [session, setSession] = React.useState<Session | null>({});
  const [navigation, setNavigation] = React.useState<NavigationState>({});
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const [language, setLanguage] = React.useState(localStorage.getItem('language') || 'ko');

  const router = React.useMemo<Router>(() => {
    return {
      pathname: location.pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        navigate(String(path));
      }
    };
  }, [location.pathname, navigate]);

  const authentication = React.useMemo(() => {
    return {
      signIn: () => {
        // 로그인 후 /login 페이지로 이동
        navigate('/login'); // signIn 클릭 시 /login 페이지로 이동
      },
      signOut: () => {
        logout();
        setSession(null);
      }
    };
  }, []);

  const renewMe = async () => {
    const userResponse: TMeResponse = await getMeAPI();
    const userData: TResponseMeData = userResponse.data;
    setUser(userData);
  };

  useEffect(() => {
    renewMe(); // 경로가 변경될 때마다 실행
  }, [location.pathname]);

  const menus = {
    // 모든 사용자
    allMember: [
      {
        name: 'notice',
        order: 1,
        segment: 'notice',
        title: t('menu.notice'),
        icon: <CampaignIcon />
      },
      {
        name: 'manual',
        order: 91,
        segment: 'manual',
        title: t('menu.manual'),
        icon: <MenuBookIcon />
      },
      {
        name: 'faq',
        order: 92,
        segment: 'faq',
        title: t('menu.faq'),
        icon: <HelpIcon />
      }
    ],
    // 내방신청 가능한 사람 (보안 경비원 제외한 전부)
    visitRequest: [
      {
        name: 'visitRequest',
        order: 31,
        segment: 'visit/request',
        title: t('menu.visit_request'),
        icon: <EventIcon />
      },
      {
        name: 'visitHistory',
        order: 32,
        segment: 'visit/history',
        title: t('menu.visit_history'),
        icon: <AccessTimeIcon />
      }
    ],
    // 시스템 관리자
    systemAdmin: [
      {
        name: 'settings',
        order: 11,
        segment: 'settings',
        title: t('menu.settings.settings'),
        icon: <SettingsIcon />,
        children: [
          {
            segment: 'system',
            title: t('menu.settings.system'),
            icon: <UploadFileIcon />
          },
          {
            segment: 'policy',
            title: t('menu.settings.policy'),
            icon: <DescriptionIcon />
          },
          {
            segment: 'item',
            title: t('menu.settings.item'),
            icon: <PostAddIcon />
          },
          {
            segment: 'group',
            title: t('menu.settings.group'),
            icon: <GroupsIcon />
          },
          {
            segment: 'user',
            title: t('menu.settings.user'),
            icon: <AccountCircleIcon />
          },
          {
            segment: 'employee',
            title: t('menu.settings.employee'),
            icon: <NotificationsActiveIcon />
          }
        ]
      },
      {
        name: 'history',
        order: 21,
        segment: 'history',
        title: t('menu.history.title'),
        icon: <HistoryIcon />,
        children: [
          {
            name: 'historyUser',
            segment: 'user',
            title: t('menu.history.user'),
            icon: <AssignmentIndIcon />
          },
          {
            name: 'historyScreen',
            segment: 'screen',
            title: t('menu.history.screen'),
            icon: <TvIcon />
          }
        ]
      }
    ],
    // 일반 관리자
    admin: [
      {
        name: 'settings',
        order: 12,
        segment: 'settings',
        title: t('menu.settings.settings'),
        icon: <SettingsIcon />,
        children: [
          {
            segment: 'employee',
            title: t('menu.settings.employee'),
            icon: <NotificationsActiveIcon />
          }
        ]
      }
    ],
    // 보안 경비원
    securityOfficer: [
      {
        name: 'visitStatus',
        order: 71,
        segment: 'visit/status',
        title: t('menu.visit_status'),
        icon: <ListAltIcon />
      },
      {
        name: 'visitHistory',
        order: 32,
        segment: 'visit/history',
        title: t('menu.visit_history'),
        icon: <AccessTimeIcon />
      },
      {
        name: 'visitRequest',
        order: 31,
        segment: 'visit/request',
        title: t('menu.visit_request'),
        isHide: true,
        icon: <EventIcon />
      }
    ]
  };
  useEffect(() => {
    const tempNavigation: NavigationState = {};
    const user = useUserStore.getState().user;
    const tempMenus = menus.allMember;

    // 그룹 별로 접속 가능한 페이지 할당
    // 그룹이 여러 개일 수도 있으니 else 사용하지 않음
    if (user?.groups.some((group) => group.type === 'SYSTEM_ADMIN')) {
      tempMenus.push(...menus.systemAdmin);
      tempMenus.push(...menus.visitRequest);
      tempMenus.push(...menus.securityOfficer);
    }

    if (user?.groups.some((group) => group.type === 'ADMIN')) {
      tempMenus.push(...menus.admin);
      tempMenus.push(...menus.visitRequest);
    }

    if (user?.groups.some((group) => group.type === 'USER')) {
      tempMenus.push(...menus.visitRequest);
    }

    if (user?.groups.some((group) => group.type === 'EMPLOYEE')) {
      tempMenus.push(...menus.visitRequest);
    }

    if (user?.groups.some((group) => group.type === 'SECURITY_OFFICER')) {
      tempMenus.push(...menus.securityOfficer);
    }

    // 순서 정렬해서 네비게이션에 셋팅
    const sortedMenus = tempMenus.sort((a, b) => a.order - b.order);
    sortedMenus.forEach((menu) => {
      tempNavigation[menu.name] = menu;
    });
    setNavigation(tempNavigation);

    user &&
      setSession({
        user: {
          name: user.name,
          email: user.email
        }
      });

    if (location.pathname === '/') {
      if (user?.groups.some((group) => group.type === 'SECURITY_OFFICER')) {
        navigate('/visit/status', { replace: true });
      } else {
        navigate('/visit/history', { replace: true });
      }
    }
  }, [useUserStore.getState().user, language]);

  const LOGO = {
    logo: (
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems="center" gap={{ xs: 0, sm: 2 }} whiteSpace="nowrap">
        <Box width={{ xs: 'auto', sm: '100%' }} maxWidth="176px" height={{ xs: '20px', sm: '100%' }}>
          <img src="/images/ulvac_logo.png" alt="ulvac_logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box fontSize={{ xs: '16px', sm: '24px' }} sx={{ fontWeight: 'bold' }}>
            {t('ulvac_korea')}
          </Box>
          <Box fontSize={{ xs: '16px', sm: '24px' }}>{t('visitor_registration_system')}</Box>
        </Box>
      </Box>
    ),
    title: ''
  };
  const AccountCustomMenu = () => {
    return (
      <Account
        slots={{
          menuItems: (props) => (user?.groups.some((group) => group.type === 'USER') ? <CustomMenu {...props} /> : null)
        }}
      />
    );
  };

  const renderContent = () => {
    const pathname = location.pathname;

    if (pathname === '/my-page') {
      return user?.groups.some((group) => group.type === 'USER') ? <EditUserInfo /> : <NotFound />;
    } else if (pathname === '/settings/system') {
      return navigation['settings']?.children?.some((child) => child.segment === 'system') ? <SystemSettings /> : <NotFound />;
    } else if (pathname === '/settings/policy') {
      return navigation['settings']?.children?.some((child) => child.segment === 'policy') ? <PolicyManagement /> : <NotFound />;
    } else if (pathname === '/settings/item') {
      return navigation['settings']?.children?.some((child) => child.segment === 'item') ? <ItemManagement /> : <NotFound />;
    } else if (pathname === '/settings/group') {
      return navigation['settings']?.children?.some((child) => child.segment === 'group') ? <GroupManagement /> : <NotFound />;
    } else if (pathname === '/settings/user') {
      return navigation['settings']?.children?.some((child) => child.segment === 'user') ? <UserManagement /> : <NotFound />;
    } else if (pathname === '/settings/employee') {
      return navigation['settings']?.children?.some((child) => child.segment === 'employee') ? <EmployeeManagement /> : <NotFound />;
    } else if (pathname === '/history/user') {
      return navigation['history']?.children?.some((child) => child.segment === 'user') ? <HistoryUser /> : <NotFound />;
    } else if (pathname === '/history/screen') {
      return navigation['history']?.children?.some((child) => child.segment === 'screen') ? <HistoryScreen /> : <NotFound />;
    } else if (pathname === '/visit/request') {
      return navigation['visitRequest'] ? <VisitRequest /> : <NotFound />;
    } else if (matchPath('/visit/request/:id', pathname)) {
      // 경로가 '/visit/request/숫자' 형식에 일치하는지 확인    // 동적 경로 매칭
      return navigation['visitRequest'] ? <VisitRequest /> : <NotFound />;
    } else if (pathname === '/visit/history') {
      return navigation['visitHistory'] ? <VisitRequestDetails /> : <NotFound />;
    } else if (pathname === '/visit/status') {
      return navigation['visitStatus'] ? <VisitRequestStatus /> : <NotFound />;
    } else if (pathname === '/manual') {
      return navigation['manual'] ? <Manual /> : <NotFound />;
    } else if (pathname === '/faq') {
      return navigation['faq'] ? <Faq /> : <NotFound />;
    } else if (pathname === '/notice') {
      return navigation['notice'] ? <NoticeBoard /> : <NotFound />;
    } else if (pathname === '/notice/new') {
      // 동적 경로 매칭
      return navigation['notice'] ? <NoticeNew /> : <NotFound />;
    } else if (matchPath('/notice/:id', pathname)) {
      // 동적 경로 매칭
      return navigation['notice'] ? <NoticeDetail /> : <NotFound />;
    } else if (matchPath('/notice/edit/:id', pathname)) {
      // 동적 경로 매칭
      return navigation['notice'] ? <NoticeEdit /> : <NotFound />;
    } else {
      return <NotFound />;
    }
  };

  useEffect(() => {
    i18n.changeLanguage(language); // 언어가 변경되면 i18n의 언어를 변경

    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [language, i18n]); // language 또는 i18n이 변경될 때마다 언어를 변경

  const visibleNavigation = React.useMemo(() => {
    const filterHiddenMenus = (menus: TMenu[]): TMenu[] =>
      menus
        .filter((menu) => !menu.isHide)
        .map((menu) => ({
          ...menu,
          children: menu.children ? filterHiddenMenus(menu.children) : undefined
        }));

    return filterHiddenMenus(Object.values(navigation));
  }, [navigation]);

  return (
    <AppProvider
      navigation={Object.values(visibleNavigation)}
      branding={LOGO}
      router={router}
      theme={customAppTheme}
      window={window}
      // authentication={authentication}
      // session={session}
    >
      <Box className="rootLayout">
        <AuthenticationContext.Provider value={authentication}>
          <SessionContext.Provider value={session}>
            {/* 사이드바 접히는부분 없애는 옵션 */}
            <DashboardLayout
              disableCollapsibleSidebar
              slots={{
                toolbarAccount: AccountCustomMenu
                // 유저아이콘 클릭시, 나타나는 커스텀 메뉴!!!
              }}
            >
              {/* Header 컴포넌트
              <Header session={session} authentication={authentication} AccountCustomMenu={AccountCustomMenu()} /> */}
              {/* 안에 렌더링 될 컴포넌트들 */}
              {renderContent()}
            </DashboardLayout>
          </SessionContext.Provider>
        </AuthenticationContext.Provider>
      </Box>

      <Footer />
    </AppProvider>
  );
};
export default React.memo(CommonLayout);
